import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { callEdgeFunction } from "@/lib/edge-functions";
import { supabase } from "@/lib/supabase";
import type {
  AssignBookingInput,
  AssignSpecificRiderInput,
  ReleaseRiderDispatchInput,
} from "@bednbody/types";

/**
 * The rider FIFO as it actually is — dispatch_queue rows with the live
 * status (available | assigned | offline, 0006) and the booking a rider is
 * currently on. Plain RLS read: dq_hub_read scopes a hub_manager to their
 * hub regardless of filters; admin/superadmin see every hub. Live GPS stays
 * on /live-map (broadcast stream) — this list is queue truth, not positions.
 */
export function useRiderQueue(hubId: string | null) {
  return useQuery({
    queryKey: ["rider-queue", hubId],
    queryFn: async () => {
      let query = supabase
        .from("dispatch_queue")
        .select(
          "rider_id, hub_id, status, booking_id, available_since, staff ( full_name ), hubs ( name ), bookings ( ref, status )",
        )
        .order("available_since");
      if (hubId) query = query.eq("hub_id", hubId);
      const { data, error } = await query;
      if (error) throw error;
      return data;
    },
    refetchInterval: 15_000,
  });
}

export type RiderQueueRow = NonNullable<ReturnType<typeof useRiderQueue>["data"]>[number];

/**
 * Today's ASAP fairness queue (migration 0036, Flow 13/14) — the
 * therapist-side counterpart to `useRiderQueue`. Same RLS shape:
 * `asap_queue_entries_hub_read` scopes a hub_manager to their hub regardless
 * of filters; admin/executive/superadmin see every hub
 * (`asap_queue_entries_admin_read`). A therapist's OWN read is a different,
 * narrower policy (own row only, SECURITY §9a) — not this hook, which is
 * staff-facing surfaces only.
 */
export function useTherapistQueue(hubId: string | null) {
  return useQuery({
    queryKey: ["therapist-queue", hubId],
    queryFn: async () => {
      const today = new Intl.DateTimeFormat("en-CA", { timeZone: "Asia/Manila" }).format(
        new Date(),
      );
      let query = supabase
        .from("asap_queue_entries")
        .select(
          "staff_id, hub_id, position, tier, disposition, moved_seq, prior_position, staff ( full_name ), hubs ( name )",
        )
        .eq("queue_date", today)
        .order("position");
      if (hubId) query = query.eq("hub_id", hubId);
      const { data, error } = await query;
      if (error) throw error;
      return data;
    },
    refetchInterval: 15_000,
  });
}

export type TherapistQueueRow = NonNullable<ReturnType<typeof useTherapistQueue>["data"]>[number];

/**
 * Therapists waiting to be collected — `therapist_dispatches` rows still
 * `waiting_for_pick_up` (0111/0237), the leg that produced three separate
 * live incidents in one afternoon (2026-09-23), every one invisible from
 * here until now. Plain RLS read: `therapist_dispatch_admin` gives
 * admin/executive/superadmin every hub unconditionally (0111); scoped
 * client-side to `hubId` the same way the two queues above are, for a
 * hub-scoped caller.
 *
 * Both names, not just the therapist's: a stuck leg is exactly as often "the
 * rider isn't moving" as "the therapist never got a rider assigned" (rider
 * nullable — the sweep may not have picked one yet), and telling them apart
 * needs both.
 */
export function useTherapistsAwaitingPickup(hubId: string | null) {
  return useQuery({
    queryKey: ["therapists-awaiting-pickup", hubId],
    queryFn: async () => {
      let query = supabase
        .from("therapist_dispatches")
        .select(
          "id, hub_id, destination, created_at, therapist:staff!therapist_dispatches_therapist_id_fkey ( full_name ), rider:staff!therapist_dispatches_rider_id_fkey ( full_name )",
        )
        .eq("status", "waiting_for_pick_up")
        .order("created_at");
      if (hubId) query = query.eq("hub_id", hubId);
      const { data, error } = await query;
      if (error) throw error;
      return data;
    },
    refetchInterval: 15_000,
  });
}

export type TherapistAwaitingPickupRow = NonNullable<
  ReturnType<typeof useTherapistsAwaitingPickup>["data"]
>[number];

/** A booking status a therapist entry is still live on — not yet Completed or cancelled. */
const THERAPIST_ACTIVE_STATUSES = [
  "awaiting_rider",
  "dispatched",
  "en_route_to_client",
  "arrived",
  "in_session",
] as const;

/**
 * The booking each queued therapist is currently on, with the facts
 * `expectedServiceEnd` (packages/logic) needs — so the Dispatch queue can show
 * "On a job (35m left)" instead of a bare "On a job" the reader has to take on
 * faith. `prior_position !== null` (see the queue filter above) means assigned
 * and not yet finished, but `asap_queue_entries` itself carries no booking
 * reference — this is that missing join, scoped the same way `useTherapistQueue`
 * is (hub filter only; RLS already scopes a hub_manager to their own hub).
 */
export function useTherapistActiveBookings(hubId: string | null) {
  return useQuery({
    queryKey: ["therapist-active-bookings", hubId],
    queryFn: async () => {
      let query = supabase
        .from("bookings")
        .select(
          "therapist_id, status, scheduled_at, service_start_deadline_at, service_ends_at, service_variants ( duration_minutes )",
        )
        .in("status", THERAPIST_ACTIVE_STATUSES)
        .not("therapist_id", "is", null);
      if (hubId) query = query.eq("hub_id", hubId);
      const { data, error } = await query;
      if (error) throw error;
      return data;
    },
    refetchInterval: 15_000,
  });
}

export type TherapistActiveBookingRow = NonNullable<
  ReturnType<typeof useTherapistActiveBookings>["data"]
>[number];

/**
 * Re-triggers the FIFO rider pick for one booking (POST /dispatch/assign,
 * already built) — "override dispatch within my hub" / "fix a stuck
 * assignment" (User Stories — Admin §Hub Manager). This does NOT let you pick
 * a specific rider; assign_rider (0006) always takes the longest-waiting
 * available rider. If none is free it throws NO_RIDER_AVAILABLE, which is the
 * expected "still queued" outcome, not a bug.
 */
export function useAssignDispatch() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: async (input: AssignBookingInput) => {
      return callEdgeFunction("dispatch/assign", { data: input });
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["ops-bookings"] });
      queryClient.invalidateQueries({ queryKey: ["active-riders"] });
    },
  });
}

/**
 * Override on top of the FIFO above — put a SPECIFIC rider on a specific
 * booking (POST /dispatch/assign-rider, 0053), bypassing assign_rider's
 * queue order. Pre-pickup only; the Edge Function bumps whoever currently
 * holds the booking back to available first.
 */
export function useAssignSpecificRider() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: async (input: AssignSpecificRiderInput) => {
      return callEdgeFunction("dispatch/assign-rider", { data: input });
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["rider-queue"] });
      queryClient.invalidateQueries({ queryKey: ["ops-bookings"] });
      queryClient.invalidateQueries({ queryKey: ["active-riders"] });
    },
  });
}

/**
 * Cancel a rider's current dispatch without picking a replacement
 * (POST /dispatch/riders/:id/release, 0053) — the recovery path for a rider
 * wedged at status='assigned' with no live booking behind it (blocks
 * clock-out forever), and the manual "wrong rider, undo it" path. Refuses if
 * the rider already has a live trip.
 */
export function useReleaseRiderDispatch() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: async (input: { riderId: string } & ReleaseRiderDispatchInput) => {
      const { riderId, ...body } = input;
      return callEdgeFunction(`dispatch/riders/${riderId}/release`, { data: body });
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["rider-queue"] });
      queryClient.invalidateQueries({ queryKey: ["ops-bookings"] });
      queryClient.invalidateQueries({ queryKey: ["active-riders"] });
      queryClient.invalidateQueries({ queryKey: ["attendance"] });
    },
  });
}
